import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { Select } from '@shared/inputs/Select';
import { TextInput } from '@shared/inputs/TextInput';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Modal } from '@shared/components/modals/Modal';
import { IOption, IPeriod } from '@shared/interfaces';
import { ModalFooter, StyledDialogContent } from '@styles/ModalStyles';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { ITeamTypeType } from '../../../interfaces';
import { NTeamManager } from '../interfaces';

interface IEditTeamModalProps {
  team: NTeamManager.Team;
  periods: IPeriod[];
  teamTypes: ITeamTypeType[];
  editTeamModalPosition: number;
  showValidation: boolean;
  handleSubmit(team: NTeamManager.Team): void;
  handleDelete(): void;
  handleClose(): void;
}

export const EditTeamModal = ({
  team,
  handleSubmit,
  handleClose,
  handleDelete,
  periods,
  editTeamModalPosition,
  teamTypes,
  showValidation
}: IEditTeamModalProps) => {
  const [name, setName] = useState(team.name);
  const [periodId, setPeriodId] = useState(team.periodId);
  const [teamTypeId, setTeamTypeId] = useState(team.teamTypeId);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const isValid = useMemo(() => (name || periodId || teamTypeId), [name, periodId, teamTypeId]);
 
  useEffect(() => {
    setName(team.name);
  }, [team.name]);

  const periodsOptions: IOption[] = useMemo(
    () =>
      periods.map((period) => ({
        value: period.id,
        label: period.name,
      })),
    [periods],
  );

  const teamTypesOptions: IOption[] = useMemo(
    () =>
      teamTypes.map((teamType) => ({
        value: teamType.id,
        label: teamType.name,
      })),
    [teamTypes],
  );

  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    isValid &&
    handleSubmit({
      ...team,
      name,
      periodId,
      teamTypeId,
    });
  },[isValid, handleSubmit, team, name, periodId, teamTypeId]);

  return (
    <Modal 
      open
      hideBackdrop
      resetPosition={editTeamModalPosition}
      title={`${team?.id ? 'Edit' : 'Create'} Team`}
      dialogWidth="700px" 
      onClose={handleClose}
    >
      <StyledDialogContent>
        <form onSubmit={onSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextInput
                label="Team Name"
                value={name}
                inputProps={{ autoFocus: true }}
                helperText={showValidation && !name ? 'Team Name is required' : ''}
                error={showValidation && !name}
                onChange={({ target: { value } }) => setName(value)}
              />
            </Grid>

            <Grid item xs={6}>
              <Select
                label="Period"
                value={periodId}
                options={periodsOptions}
                placeholder="Select Period..."
                helperText={showValidation && !periodId ? 'Period is required' : ''}
                error={showValidation && !periodId}
                onChange={setPeriodId}
              />
            </Grid>

            <Grid item xs={6}>
              <Select
                label="Team Type"
                value={teamTypeId}
                options={teamTypesOptions}
                helperText={showValidation && !teamTypeId ? 'Team Type is required' : ''}
                error={showValidation && !teamTypeId}
                placeholder="Select Team Type..."
                onChange={setTeamTypeId}
              />
            </Grid>

            <Grid item xs={12}>
              <ModalFooter style={{ gap: 0 }} margin="0 0 10px">
              {!team?.isNew && (
                  <CancelButton
                    label="Delete Team"
                    variant="contained"
                    onClick={() => setShowDeleteModal(true)}
                  />
                )}
                <CancelButton onClick={handleClose} />
                <SaveButton />
              </ModalFooter>
            </Grid>
          </Grid>
        </form>

        <AlertModal
          title={`Delete team - "${name}"`}
          subtitle={'Are you sure you want to delete this team?'}
          open={showDeleteModal}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteModal(false)}
        />
      </StyledDialogContent>
    </Modal>
  );
};
